import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Search, Camera, Pencil, Star } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useSession } from "@/hooks/use-session";
import {
  Drawer,
  useToast,
  SkeletonLoader,
  EmptyState,
} from "@/components/system";
import { useFoodSearch, type FoodSearchHit } from "@/hooks/use-food-search";
import { useAthleteProfile } from "@/hooks/use-athlete-profile";
import { offToFoodInsert } from "@/api/openfoodfacts";
import {
  Row,
  FormInput,
  PrimaryButton,
  SecondaryButton,
  DialogFooter,
  localNowDatetime,
  browserTz,
} from "@/components/forms";
import { formatError } from "@/lib/format-error";
import { MealPhotoTab } from "./MealPhotoTab";

/**
 * Meal log with food search (SPEC 2.2.1 + 3.1.4).
 *
 * Flow:
 *   1. User types a query → cache + OFF results.
 *   2. Picking a result fills name + kcal/macros (scaled by qty).
 *      OFF hits get promoted to a `food` row first so the meal references
 *      a real food_id.
 *   3. Manual entry fallback: skip search, type fields directly.
 *
 * Insert: `meal` row with foods=[{food_id, qty, unit}] when a food was
 * picked, otherwise foods=[] (manual). source='manual' regardless.
 */

type PickedFood = {
  food_id: string;
  name: string;
  brand: string | null;
  kcalPer100: number;
  basis: string;
};

type TabKey = "photo" | "search" | "manual" | "favorites";

type FavoriteRow = {
  user_id: string;
  source_recipe_id: string | null;
  display_name: string | null;
  logged_count: number;
  avg_kcal: number | null;
  avg_protein: number | null;
  avg_carbs: number | null;
  avg_fat: number | null;
  last_logged_at: string | null;
};

export function LogMealDialog({
  open,
  onClose,
  initialTab,
  initialFiles,
}: {
  open: boolean;
  onClose: () => void;
  initialTab?: TabKey;
  initialFiles?: File[];
}) {
  const { session } = useSession();
  const userId = session?.user?.id;
  const qc = useQueryClient();
  const toast = useToast();
  const profile = useAthleteProfile();
  const onGlp1 = Boolean(profile.data?.on_glp1);

  // BW18.5 PR-5.D: Photo demoted to last position. Search is the safer
  // default since OFF / pantry hits carry verified macros; photo
  // extraction's portion estimates are uncertain enough that they should
  // not silently land in nutrition rolls without review.
  const [tab, setTab] = useState<TabKey>(initialTab ?? "search");
  const [query, setQuery] = useState("");
  const [picked, setPicked] = useState<PickedFood | null>(null);
  const [qtyG, setQtyG] = useState("");
  const [name, setName] = useState("");
  const [kcal, setKcal] = useState("");
  const [protein, setProtein] = useState("");
  const [carb, setCarb] = useState("");
  const [fat, setFat] = useState("");
  const [ts, setTs] = useState(localNowDatetime());
  const [hungerBefore, setHungerBefore] = useState<string>("");
  const [feltNauseous, setFeltNauseous] = useState<boolean | null>(null);

  const { hits, isLoading: searchLoading } = useFoodSearch(query);

  // Promote an OFF hit to a food row so we have a stable food_id.
  const promote = useMutation({
    mutationFn: async (hit: Extract<FoodSearchHit, { kind: "off" }>) => {
      if (!userId) throw new Error("Not signed in");
      const insert = offToFoodInsert(hit.product, userId);
      const { data, error } = await supabase
        .from("food")
        .insert(insert)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
  });

  const logMeal = useMutation({
    mutationFn: async () => {
      if (!userId) throw new Error("Not signed in");
      const qty = qtyG ? Number(qtyG) : null;

      let mealKcal = kcal ? Number(kcal) : 0;
      const mealMacros = {
        p: protein ? Number(protein) : 0,
        c: carb ? Number(carb) : 0,
        f: fat ? Number(fat) : 0,
      };
      const foods: Array<{ food_id: string; qty: number; unit: string }> = [];

      if (picked && qty) {
        const scale =
          picked.basis === "per_100g" || picked.basis === "per_100ml"
            ? qty / 100
            : qty;
        if (!kcal) mealKcal = picked.kcalPer100 * scale;
        foods.push({
          food_id: picked.food_id,
          qty,
          unit:
            picked.basis === "per_100ml"
              ? "ml"
              : picked.basis === "per_unit"
                ? "pcs"
                : "g",
        });
      }

      const { data, error } = await supabase
        .from("meal")
        .insert({
          user_id: userId,
          ts: new Date(ts).toISOString(),
          tz: browserTz(),
          name: name.trim() || picked?.name || "Meal",
          foods,
          kcal: mealKcal,
          macros: mealMacros,
          micros: {},
          source: "manual",
          notes: "",
          locked: false,
          hunger_before:
            onGlp1 && hungerBefore !== "" ? Number(hungerBefore) : null,
          felt_nauseous: onGlp1 ? feltNauseous : null,
        })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["today_meals", userId] });
      toast.show(`${name.trim() || picked?.name || "Meal"} logged.`);
      reset();
      onClose();
    },
    onError: (e) => toast.show(`Couldn't log meal: ${formatError(e)}`),
  });

  function reset() {
    setQuery("");
    setPicked(null);
    setQtyG("");
    setName("");
    setKcal("");
    setProtein("");
    setCarb("");
    setFat("");
    setTs(localNowDatetime());
    setHungerBefore("");
    setFeltNauseous(null);
  }

  async function pickHit(hit: FoodSearchHit) {
    let foodId: string | null;
    let basis: string;
    if (hit.kind === "cache") {
      foodId = hit.id;
      basis = hit.basis;
    } else if (hit.kind === "pantry") {
      foodId = hit.foodId;
      basis = hit.basis;
    } else {
      try {
        const promoted = await promote.mutateAsync(hit);
        foodId = promoted.id;
        basis = promoted.basis;
      } catch (e) {
        toast.show(`Couldn't promote OFF item: ${formatError(e)}`);
        return;
      }
    }
    setPicked({
      food_id: foodId ?? "",
      name: hit.name,
      brand: hit.brand,
      kcalPer100: hit.kcalPer100,
      basis,
    });
    setName(hit.name);
    if (!qtyG) setQtyG(basis === "per_unit" ? "1" : "100");
  }

  function applyFavorite(row: FavoriteRow) {
    setName(row.display_name ?? "Meal");
    setKcal(row.avg_kcal != null ? String(Math.round(Number(row.avg_kcal))) : "");
    setProtein(
      row.avg_protein != null ? String(Math.round(Number(row.avg_protein))) : "",
    );
    setCarb(
      row.avg_carbs != null ? String(Math.round(Number(row.avg_carbs))) : "",
    );
    setFat(row.avg_fat != null ? String(Math.round(Number(row.avg_fat))) : "");
    setTs(localNowDatetime());
    setPicked(null);
    setQtyG("");
    setTab("manual");
  }

  return (
    <Drawer
      open={open}
      onClose={() => {
        reset();
        onClose();
      }}
      title="Log meal"
      size="lg"
    >
      <TabBar tab={tab} onChange={setTab} />
      {tab === "photo" && (
        <MealPhotoTab
          onLogged={() => {
            onClose();
          }}
          initialFiles={initialFiles}
        />
      )}
      {tab === "search" && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            logMeal.mutate();
          }}
          style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}
        >
          <div
            style={{
              position: "relative",
              display: "flex",
              alignItems: "center",
              gap: "var(--space-2)",
            }}
          >
            <Search
              size={16}
              strokeWidth={1.5}
              style={{
                position: "absolute",
                left: "var(--space-3)",
                color: "var(--fg-3)",
                pointerEvents: "none",
              }}
            />
            <input
              type="search"
              placeholder="Search food (e.g. oats, banana, chicken)"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              style={{
                flex: 1,
                minWidth: 0,
                height: 38,
                padding: "0 var(--space-3) 0 calc(var(--space-3) + 22px)",
                background: "var(--bg-3)",
                color: "var(--fg)",
                border: "1px solid var(--hair-2)",
                borderRadius: "var(--radius-card)",
                fontFamily: "var(--font-sans)",
                fontSize: "var(--type-body)",
                boxSizing: "border-box",
              }}
            />
          </div>

          {picked ? (
            <PickedRow
              picked={picked}
              onClear={() => {
                setPicked(null);
                setName("");
                setQuery("");
              }}
            />
          ) : query.trim().length >= 2 ? (
            <Results
              hits={hits}
              isLoading={searchLoading}
              isPromoting={promote.isPending}
              onPick={pickHit}
              onSwitchToManual={() => setTab("manual")}
            />
          ) : (
            <EmptyState
              headline="Search for a food"
              sub="Type at least 2 letters. Pantry items and your past meals show first; OpenFoodFacts fills in the rest."
            />
          )}

          {picked && (
            <Row>
              <FormInput
                label={
                  picked.basis === "per_100ml"
                    ? "Quantity (ml)"
                    : picked.basis === "per_unit"
                      ? "Quantity (pcs)"
                      : "Quantity (g)"
                }
                inputMode="decimal"
                value={qtyG}
                onChange={setQtyG}
                placeholder={picked.basis === "per_unit" ? "1" : "100"}
              />
              <FormInput
                label="When"
                type="datetime-local"
                value={ts}
                onChange={setTs}
              />
            </Row>
          )}

          {onGlp1 && picked && (
            <GlpMealFields
              hungerBefore={hungerBefore}
              setHungerBefore={setHungerBefore}
              feltNauseous={feltNauseous}
              setFeltNauseous={setFeltNauseous}
            />
          )}

          <DialogFooter>
            <SecondaryButton
              onClick={() => {
                reset();
                onClose();
              }}
              disabled={logMeal.isPending}
            >
              Cancel
            </SecondaryButton>
            <PrimaryButton
              type="submit"
              disabled={logMeal.isPending || !picked || !qtyG}
            >
              {logMeal.isPending ? "Saving…" : "Log meal"}
            </PrimaryButton>
          </DialogFooter>
        </form>
      )}

      {tab === "manual" && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            logMeal.mutate();
          }}
          style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}
        >
          <FormInput
            label="What did you eat?"
            value={name}
            onChange={setName}
            placeholder="Oats with peanut butter"
          />
          <Row>
            <FormInput
              label="kcal"
              inputMode="decimal"
              value={kcal}
              onChange={setKcal}
              placeholder="450"
            />
            <FormInput
              label="When"
              type="datetime-local"
              value={ts}
              onChange={setTs}
            />
          </Row>
          <Row>
            <FormInput
              label="Protein (g)"
              inputMode="decimal"
              value={protein}
              onChange={setProtein}
              placeholder="30"
            />
            <FormInput
              label="Carbs (g)"
              inputMode="decimal"
              value={carb}
              onChange={setCarb}
              placeholder="50"
            />
            <FormInput
              label="Fat (g)"
              inputMode="decimal"
              value={fat}
              onChange={setFat}
              placeholder="15"
            />
          </Row>

          {onGlp1 && (
            <GlpMealFields
              hungerBefore={hungerBefore}
              setHungerBefore={setHungerBefore}
              feltNauseous={feltNauseous}
              setFeltNauseous={setFeltNauseous}
            />
          )}

          <DialogFooter>
            <SecondaryButton
              onClick={() => {
                reset();
                onClose();
              }}
              disabled={logMeal.isPending}
            >
              Cancel
            </SecondaryButton>
            <PrimaryButton
              type="submit"
              disabled={logMeal.isPending || (!name && !kcal)}
            >
              {logMeal.isPending ? "Saving…" : "Log meal"}
            </PrimaryButton>
          </DialogFooter>
        </form>
      )}

      {tab === "favorites" && (
        <FavoritesTab onPick={applyFavorite} />
      )}
    </Drawer>
  );
}

function TabBar({
  tab,
  onChange,
}: {
  tab: TabKey;
  onChange: (k: TabKey) => void;
}) {
  // BW17 Issue 3: 4-tab spec from BW12 §22 (Photo / Search / Manual /
  // Favorites). The Favorites tab pulls from `meal_favorites_derived` —
  // any logged meal hit ≥3× in the last 60 days.
  // BW18.5 PR-5.D: Photo last. Each tab's data path differs in trust:
  //   Search → OFF / cache → verified macros
  //   Manual → user-entered → user-trusted
  //   Favorites → re-log of past meal → user-trusted
  //   Photo → AI extraction → portion uncertain
  const tabs: Array<{ key: TabKey; label: string; Icon: typeof Search }> = [
    { key: "search", label: "Search", Icon: Search },
    { key: "manual", label: "Manual", Icon: Pencil },
    { key: "favorites", label: "Favorites", Icon: Star },
    { key: "photo", label: "Photo", Icon: Camera },
  ];
  return (
    <div
      role="tablist"
      style={{
        display: "grid",
        gridTemplateColumns: `repeat(${tabs.length}, minmax(0, 1fr))`,
        gap: 4,
        background: "var(--bg-3)",
        borderRadius: "var(--radius-card)",
        padding: 4,
        marginBottom: "var(--space-3)",
      }}
    >
      {tabs.map((t) => {
        const active = tab === t.key;
        return (
          <button
            key={t.key}
            role="tab"
            aria-selected={active}
            type="button"
            onClick={() => onChange(t.key)}
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 6,
              padding: "8px 8px",
              minWidth: 0,
              background: active ? "var(--bg)" : "transparent",
              border: "none",
              borderRadius: "var(--radius-chip)",
              color: active ? "var(--fg)" : "var(--fg-3)",
              fontSize: "var(--type-body-s)",
              cursor: "pointer",
              fontWeight: active ? 500 : 400,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            <t.Icon size={14} strokeWidth={1.6} />
            {t.label}
          </button>
        );
      })}
    </div>
  );
}

function FavoritesTab({ onPick }: { onPick: (row: FavoriteRow) => void }) {
  const { session } = useSession();
  const userId = session?.user?.id;
  const q = useQuery({
    queryKey: ["meal_favorites_derived", userId],
    enabled: Boolean(userId),
    staleTime: 60_000,
    queryFn: async (): Promise<FavoriteRow[]> => {
      if (!userId) return [];
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const supa = supabase as unknown as { from: (t: string) => any };
      const { data, error } = await supa
        .from("meal_favorites_derived")
        .select(
          "user_id, source_recipe_id, display_name, logged_count, avg_kcal, avg_protein, avg_carbs, avg_fat, last_logged_at",
        )
        .eq("user_id", userId)
        .order("logged_count", { ascending: false })
        .limit(20);
      if (error) throw new Error(error.message ?? "Couldn't load favorites.");
      return (data ?? []) as FavoriteRow[];
    },
  });

  if (q.isLoading) return <SkeletonLoader variant="block" count={3} />;
  if (q.isError) {
    return (
      <p role="alert" style={{ color: "var(--brick)", fontSize: "var(--type-body-s)" }}>
        {q.error instanceof Error ? q.error.message : "Couldn't load favorites."}
      </p>
    );
  }
  const rows = q.data ?? [];
  if (rows.length === 0) {
    return (
      <EmptyState
        headline="No favorites yet"
        sub="Log a meal three or more times in the last 60 days and it'll appear here for one-tap re-log."
      />
    );
  }
  return (
    <ul
      style={{
        listStyle: "none",
        margin: 0,
        padding: 0,
        display: "flex",
        flexDirection: "column",
        gap: "var(--space-2)",
      }}
    >
      {rows.map((row, idx) => (
        <li key={`${row.source_recipe_id ?? "x"}-${row.display_name ?? idx}`}>
          <button
            type="button"
            onClick={() => onPick(row)}
            style={{
              width: "100%",
              padding: "var(--space-3)",
              background: "transparent",
              border: "1px solid var(--hair)",
              borderRadius: "var(--radius-card)",
              textAlign: "left",
              cursor: "pointer",
              display: "grid",
              gridTemplateColumns: "1fr max-content",
              gap: "var(--space-3)",
              alignItems: "baseline",
            }}
          >
            <div style={{ minWidth: 0 }}>
              <div
                style={{
                  color: "var(--fg)",
                  fontSize: "var(--type-body-s)",
                  fontWeight: 500,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {row.display_name ?? "Meal"}
              </div>
              <div
                style={{
                  color: "var(--fg-3)",
                  fontSize: "var(--type-meta)",
                  marginTop: 2,
                }}
              >
                <span className="mono">{row.logged_count}×</span>
                <span style={{ color: "var(--fg-4)", margin: "0 6px" }}>·</span>
                <span className="mono">
                  {row.avg_kcal != null
                    ? `${Math.round(Number(row.avg_kcal))} kcal`
                    : "—"}
                </span>
                {row.avg_protein != null && (
                  <>
                    <span style={{ color: "var(--fg-4)", margin: "0 6px" }}>·</span>
                    <span className="mono">
                      P {Math.round(Number(row.avg_protein))}g
                    </span>
                  </>
                )}
              </div>
            </div>
            <div
              className="mono"
              style={{ color: "var(--fg-3)", fontSize: "var(--type-meta)" }}
            >
              {row.last_logged_at
                ? new Date(row.last_logged_at).toLocaleDateString()
                : ""}
            </div>
          </button>
        </li>
      ))}
    </ul>
  );
}

function Results({
  hits,
  isLoading,
  isPromoting,
  onPick,
  onSwitchToManual,
}: {
  hits: FoodSearchHit[];
  isLoading: boolean;
  isPromoting: boolean;
  onPick: (h: FoodSearchHit) => void;
  onSwitchToManual?: () => void;
}) {
  if (isLoading) return <SkeletonLoader variant="block" count={3} />;
  if (hits.length === 0) {
    return (
      <div style={{ display: "grid", gap: "var(--space-3)" }}>
        <EmptyState
          headline="No matches"
          sub="Search now requires every word to appear in the product name or brand."
        />
        {onSwitchToManual && (
          <button
            type="button"
            onClick={onSwitchToManual}
            style={{
              alignSelf: "center",
              padding: "8px 16px",
              background: "var(--accent)",
              color: "#0a0b0d",
              border: "none",
              borderRadius: "var(--radius-card)",
              fontSize: "var(--type-body-s)",
              fontWeight: 500,
              cursor: "pointer",
            }}
          >
            Add manually →
          </button>
        )}
      </div>
    );
  }
  return (
    <ul
      style={{
        listStyle: "none",
        margin: 0,
        padding: 0,
        display: "flex",
        flexDirection: "column",
        gap: "var(--space-1)",
        maxHeight: 320,
        overflow: "auto",
      }}
    >
      {hits.map((h) => {
        const key =
          h.kind === "cache"
            ? `c-${h.id}`
            : h.kind === "pantry"
              ? `p-${h.pantryItemId}`
              : `o-${h.offCode}`;
        const sourceLabel =
          h.kind === "pantry"
            ? "Pantry"
            : h.kind === "cache"
              ? "cache"
              : "OFF";
        const sourceColor =
          h.kind === "pantry" ? "var(--accent)" : "var(--fg-3)";
        const subtle =
          h.kind === "pantry"
            ? `${h.qtyRemaining ?? "—"} ${h.unit}${h.expiresAt ? ` · exp ${h.expiresAt}` : ""}`
            : null;
        return (
          <li key={key}>
            <button
              type="button"
              onClick={() => onPick(h)}
              disabled={isPromoting}
              style={{
                width: "100%",
                padding: "var(--space-3)",
                background: h.kind === "pantry" ? "var(--accent-wash)" : "transparent",
                border: `1px solid ${h.kind === "pantry" ? "var(--accent-dim)" : "var(--hair)"}`,
                borderRadius: "var(--radius-card)",
                textAlign: "left",
                cursor: "pointer",
                display: "grid",
                gridTemplateColumns: "1fr max-content",
                gap: "var(--space-3)",
                alignItems: "baseline",
              }}
            >
              <div>
                <div style={{ color: "var(--fg)", fontSize: "var(--type-body-s)" }}>
                  {h.name}
                </div>
                <div
                  style={{
                    color: "var(--fg-3)",
                    fontSize: "var(--type-meta)",
                    marginTop: 2,
                  }}
                >
                  {h.brand ?? "—"}
                  <span style={{ color: "var(--fg-4)", margin: "0 6px" }}>·</span>
                  <span
                    className="mono"
                    style={{
                      textTransform: "uppercase",
                      letterSpacing: "0.04em",
                      color: sourceColor,
                    }}
                  >
                    {sourceLabel}
                  </span>
                  {subtle && (
                    <>
                      <span style={{ color: "var(--fg-4)", margin: "0 6px" }}>·</span>
                      <span className="mono" style={{ color: "var(--fg-3)" }}>
                        {subtle}
                      </span>
                    </>
                  )}
                </div>
              </div>
              <div
                className="mono"
                style={{ color: "var(--fg-2)", fontSize: "var(--type-body-s)" }}
              >
                {Math.round(h.kcalPer100)} kcal/100g
              </div>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

/**
 * Optional GLP-1 logging fields (BW18 PR-I).
 *
 * Shown only when athlete_profile.on_glp1 is true. Hunger before eating
 * (0-10) feeds the trend chart in /health/glp1; nausea-after-meal flag
 * powers the nausea-trigger pattern detector after enough data points.
 */
export function GlpMealFields({
  hungerBefore,
  setHungerBefore,
  feltNauseous,
  setFeltNauseous,
}: {
  hungerBefore: string;
  setHungerBefore: (v: string) => void;
  feltNauseous: boolean | null;
  setFeltNauseous: (v: boolean | null) => void;
}) {
  return (
    <div
      style={{
        padding: "var(--space-3)",
        background: "var(--bg-3)",
        border: "1px solid var(--hair)",
        borderRadius: "var(--radius-card)",
        display: "grid",
        gap: "var(--space-3)",
      }}
    >
      <p
        style={{
          margin: 0,
          fontSize: "var(--type-meta)",
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          color: "var(--fg-3)",
        }}
      >
        GLP-1 calibration · optional
      </p>
      <label style={{ display: "grid", gap: "var(--space-1)" }}>
        <span
          style={{
            fontSize: "var(--type-body-s)",
            color: "var(--fg)",
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <span>Hunger before eating</span>
          <span className="mono" style={{ color: "var(--fg-3)" }}>
            {hungerBefore === "" ? "—" : `${hungerBefore} / 10`}
          </span>
        </span>
        <input
          type="range"
          min={0}
          max={10}
          step={1}
          value={hungerBefore === "" ? 5 : Number(hungerBefore)}
          onChange={(e) => setHungerBefore(e.target.value)}
          aria-label="Hunger before eating, 0 to 10"
          style={{ width: "100%" }}
        />
        <span
          style={{
            fontSize: "var(--type-meta)",
            color: "var(--fg-3)",
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <span>Not hungry</span>
          <span>Ravenous</span>
        </span>
      </label>
      <fieldset
        style={{
          display: "grid",
          gridTemplateColumns: "1fr auto auto auto",
          alignItems: "center",
          gap: "var(--space-2)",
          border: "none",
          padding: 0,
          margin: 0,
        }}
      >
        <legend style={{ fontSize: "var(--type-body-s)", color: "var(--fg)" }}>
          Felt nauseous after?
        </legend>
        <NauseaButton
          active={feltNauseous === false}
          onClick={() => setFeltNauseous(feltNauseous === false ? null : false)}
        >
          No
        </NauseaButton>
        <NauseaButton
          active={feltNauseous === true}
          onClick={() => setFeltNauseous(feltNauseous === true ? null : true)}
        >
          Yes
        </NauseaButton>
      </fieldset>
    </div>
  );
}

function NauseaButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      style={{
        padding: "4px 14px",
        background: active ? "var(--chip-hi)" : "transparent",
        color: active ? "var(--fg)" : "var(--fg-3)",
        border: `1px solid ${active ? "var(--accent-dim)" : "var(--hair-2)"}`,
        borderRadius: "var(--radius-chip)",
        fontSize: "var(--type-body-s)",
        cursor: "pointer",
      }}
    >
      {children}
    </button>
  );
}

function PickedRow({
  picked,
  onClear,
}: {
  picked: PickedFood;
  onClear: () => void;
}) {
  return (
    <div
      style={{
        padding: "var(--space-3)",
        background: "var(--accent-wash)",
        border: "1px solid var(--accent-dim)",
        borderRadius: "var(--radius-card)",
        display: "flex",
        gap: "var(--space-3)",
        alignItems: "center",
        justifyContent: "space-between",
      }}
    >
      <div>
        <div
          style={{
            color: "var(--fg)",
            fontSize: "var(--type-body-s)",
            fontWeight: 500,
          }}
        >
          {picked.name}
        </div>
        <div
          style={{
            color: "var(--fg-3)",
            fontSize: "var(--type-meta)",
            marginTop: 2,
          }}
        >
          {picked.brand ?? "—"}
          <span style={{ color: "var(--fg-4)", margin: "0 6px" }}>·</span>
          <span className="mono">{Math.round(picked.kcalPer100)} kcal/100g</span>
        </div>
      </div>
      <button
        type="button"
        onClick={onClear}
        style={{
          background: "transparent",
          color: "var(--fg-3)",
          border: "1px solid var(--hair-2)",
          borderRadius: "var(--radius-chip)",
          padding: "4px 10px",
          fontSize: "var(--type-meta)",
          cursor: "pointer",
        }}
      >
        Clear
      </button>
    </div>
  );
}
